import { useState } from 'react';
import { Dialog, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { useWallet } from '../hooks/use-wallet';
import { Coins, Sparkles, CheckCircle2, History } from 'lucide-react';

interface FaucetModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function FaucetModal({ open, onOpenChange }: FaucetModalProps) {
  const { balance, transactions, claimFaucet } = useWallet();
  const [claimedNotice, setClaimedNotice] = useState<string | null>(null);

  const handleClaim = () => {
    const res = claimFaucet(1000);
    setClaimedNotice(`+${res.amount.toLocaleString()} PRED Coins Credited!`);
    setTimeout(() => {
      setClaimedNotice(null);
    }, 2500);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogHeader>
        <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-primary/12 text-primary mb-2">
          <Coins className="h-6 w-6" />
        </div>
        <DialogTitle className="text-xl font-bold">Free Game Coins & Faucet</DialogTitle>
        <DialogDescription className="text-xs">
          Get test prediction chips anytime to explore prediction markets and the live arena
          risk-free.
        </DialogDescription>
      </DialogHeader>

      <div className="space-y-4 my-2">
        {/* Balance card */}
        <div className="rounded-lg border bg-muted p-5 text-center">
          <span className="text-xs text-muted-foreground uppercase tracking-wider font-semibold">
            Current Wallet Balance
          </span>
          <div className="mt-1 flex items-center justify-center gap-2">
            <span className="text-3xl font-bold font-mono text-foreground">
              {balance.toLocaleString()}
            </span>
            <span className="text-sm font-medium text-muted-foreground font-mono">PRED</span>
          </div>
        </div>

        {claimedNotice ? (
          <div
            role="status"
            className="flex items-center justify-center gap-2 rounded-lg bg-success/12 border border-success/30 p-3 text-success font-bold text-sm animate-in zoom-in-95"
          >
            <CheckCircle2 className="h-5 w-5" />
            <span>{claimedNotice}</span>
          </div>
        ) : (
          <Button
            type="button"
            variant="default"
            size="lg"
            onClick={handleClaim}
            className="w-full h-12 text-base gap-2 font-bold"
          >
            <Sparkles className="h-5 w-5" />
            Claim +1,000 Free PRED Coins
          </Button>
        )}

        {/* Transaction History list */}
        <div>
          <div className="flex items-center gap-1.5 text-xs font-semibold text-muted-foreground mb-2">
            <History className="h-3.5 w-3.5" />
            <span>Recent Activity</span>
          </div>

          <div className="max-h-40 overflow-y-auto space-y-1.5 pr-1">
            {transactions.slice(0, 6).map((tx) => (
              <div
                key={tx.id}
                className="flex items-center justify-between rounded-lg border border-border/60 bg-secondary/30 px-3 py-2 text-xs font-mono"
              >
                <div className="truncate mr-2">
                  <p className="font-medium text-foreground truncate">{tx.label}</p>
                  <p className="text-xs text-muted-foreground">
                    {new Date(tx.timestamp).toLocaleTimeString()}
                  </p>
                </div>
                <span
                  className={`font-bold shrink-0 ${
                    tx.amount > 0 ? 'text-success' : 'text-destructive'
                  }`}
                >
                  {tx.amount > 0 ? `+${tx.amount.toLocaleString()}` : tx.amount.toLocaleString()}{' '}
                  PRED
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </Dialog>
  );
}
